import type { SpectralInput } from './spectral.js';

const FFT_SIZE = 1024;
const HOP_SIZE = 512;
const SIB_LO_HZ = 5000;
const SIB_HI_HZ = 10000;

/**
 * Sibilance band energy.
 *
 * Returns the energy in the 5–10 kHz band as **dB relative to full-band
 * energy** (i.e. 10·log10(E_sibilance / E_total)). This is a unitless ratio
 * so it does not depend on absolute signal level — a sibilant signal sits
 * near 0 dB, a low-passed signal sits well below 0 (very negative).
 */
export function sibilanceBandEnergyDb(input: SpectralInput): number {
  const { samples, sample_rate } = input;
  if (samples.length < FFT_SIZE || sample_rate <= 0) return -Infinity;

  const halfBins = FFT_SIZE / 2;
  const window = hannWindow(FFT_SIZE);
  const re = new Float64Array(FFT_SIZE);
  const im = new Float64Array(FFT_SIZE);
  const powSum = new Float64Array(halfBins);
  let frameCount = 0;

  for (let start = 0; start + FFT_SIZE <= samples.length; start += HOP_SIZE) {
    for (let i = 0; i < FFT_SIZE; i++) {
      re[i] = samples[start + i] * window[i];
      im[i] = 0;
    }
    fftRadix2(re, im);
    for (let k = 0; k < halfBins; k++) {
      powSum[k] += re[k] * re[k] + im[k] * im[k];
    }
    frameCount++;
  }
  if (frameCount === 0) return -Infinity;

  const binHz = sample_rate / FFT_SIZE;
  let bandPow = 0;
  let totalPow = 0;
  for (let k = 1; k < halfBins; k++) {
    const f = k * binHz;
    const p = powSum[k] / frameCount;
    totalPow += p;
    if (f >= SIB_LO_HZ && f < SIB_HI_HZ) bandPow += p;
  }

  if (totalPow <= 1e-30) return -Infinity;
  if (bandPow <= 1e-30) return -Infinity;
  return 10 * Math.log10(bandPow / totalPow);
}

function hannWindow(n: number): Float64Array {
  const w = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    w[i] = 0.5 * (1 - Math.cos((2 * Math.PI * i) / (n - 1)));
  }
  return w;
}

// In-place Cooley-Tukey radix-2 FFT. n must be a power of 2.
function fftRadix2(re: Float64Array, im: Float64Array): void {
  const n = re.length;
  let j = 0;
  for (let i = 1; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) {
      const tr = re[i];
      re[i] = re[j];
      re[j] = tr;
      const ti = im[i];
      im[i] = im[j];
      im[j] = ti;
    }
  }
  for (let size = 2; size <= n; size <<= 1) {
    const half = size >> 1;
    const theta = (-2 * Math.PI) / size;
    const wpr = Math.cos(theta);
    const wpi = Math.sin(theta);
    for (let start = 0; start < n; start += size) {
      let wr = 1;
      let wi = 0;
      for (let k = 0; k < half; k++) {
        const i0 = start + k;
        const i1 = i0 + half;
        const tr = wr * re[i1] - wi * im[i1];
        const ti = wr * im[i1] + wi * re[i1];
        re[i1] = re[i0] - tr;
        im[i1] = im[i0] - ti;
        re[i0] = re[i0] + tr;
        im[i0] = im[i0] + ti;
        const nwr = wr * wpr - wi * wpi;
        wi = wr * wpi + wi * wpr;
        wr = nwr;
      }
    }
  }
}
